<template>
    <div v-if="!isVatLiable && vatLiableLoaded" class="flex justify-center bg-black/30 w-full h-[200vh] absolute z-10 border border-base-300">
        <div class="flex flex-col">
            <alert-box
                title="Ikke MVA-pliktig"
                :message="`${selectedOrg?.organizationName} er satt som ikke MVA-pliktig i virksomhetsinnstillingene.`"
                additional-info="MVA innstillinger kan endres i virksomhetsinnstillingene."
                type="info"
                :disable-close="true"
                timeout="null"
            />
        </div>
    </div>
    <div v-if="vatLiableLoaded" class="h-full flex flex-col px-3 overflow-y-auto @xl:overflow-y-visible">
        <div class="flex flex-col @xl:flex-row justify-between">
            <!--      Info          -->
            <div v-if="vatPeriodInfo" id="status-selector" class="rounded mx-2 px-2 pt-1 text-sm columns-2 bg-primary/20 mb-4 mt-1 border border-base-300 shadow-md grid">
                <vat-period-selector
                    v-model="range"
                    class="bg-base-100 p-1 border border-base-300 rounded"
                    :vat-period-statuses="vatPeriodStatuses"
                    :vat-annually="vatAnnuallySetting.value"
                    @update:model-value="handleTermChanged"
                />
                <div class="mt-2">
                    <div class="w-1/2">
                        <span>{{ cachedT('vat-report.period') }}</span>
                        <div id="period" class="rounded p-2 bg-base-200 my-1  border border-base-300" title="For å endre periode, velg en annen termin">
                            {{ cachedT('vat-report.' + vatPeriodInfo.periodName) }} {{ vatReportToTaxAuthorities.year }}
                        </div>
                    </div>
                    <div class="grid @xl:grid-cols-2 @xl:gap-2">
                        <div class="w-1/2 @xl:w-full">
                            <span>{{ VatTotalText() }}</span>
                            <div id="vat-total" class="rounded p-2 bg-base-200  my-1  border border-base-300">
                                {{ formatCurrency(Math.abs(vatReportToTaxAuthorities.vatToPay), 0) }}
                            </div>
                        </div>
                        <div class="w-1/2 @xl:w-full">
                            <span>{{ cachedT('vat-report.payment-due') }}</span>
                            <div id="payment-due" class="rounded p-2 bg-base-200  my-1 border border-base-300">
                                {{ vatPeriodInfo.paymentDue }}
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <div>
                        <span>{{ cachedT('vat-report.comment') }}</span>
                        <textarea v-model="externalRemark" class="w-full h-full bg-base-100 rounded p-2 mb-0 my-1  border border-base-300"/>
                    </div>
                    <div>
                        <span>{{ cachedT('vat-report.internal-comment') }}</span>
                        <textarea v-model="internalRemark" class="w-full h-full bg-base-100 rounded p-2 my-1 border border-base-300"/>
                    </div>
                </div>
            </div>
            <div class="w-full flex">
                <div class="flex flex-col w-full">
                    <!--     History       -->
                    <div class="flex @xl:justify-end">
                        <div class="rounded p-2 max-h-[20.5rem] text-sm bg-primary/20 mt-1 mb-4 border border-base-300 shadow-md overflow-auto pt-1">
                            <div class="h-full">
                                <div class="h-full pb-6">
                                    <h2>{{ cachedT('vat-report.bookkeept-vat') }}</h2>
                                    <div class="rounded p-2 bg-base-100 my-1 border border-base-300 w-full h-full overflow-scroll ">
                                        <table id="vat-table" class="w-full text-left">
                                            <tbody>
                                                <tr class="border-b border-base-300">
                                                    <th class="pr-2">{{ cachedT('vat-report.name') }}</th>
                                                    <th class="pr-2">{{ cachedT('vat-report.created-at') }}</th>
                                                    <th class="pr-2">{{ cachedT('vat-report.total') }}</th>
                                                    <th>{{ cachedT('vat-report.created-by') }}</th>
                                                    <th class="px-2 truncate text-ellipsis overflow-hidden whitespace-nowrap"> {{ cachedT('vat-report.download') }}</th>
                                                </tr>
                                                <tr
                                                    v-for="(metadata, index) in vatReportsMetadata"
                                                    :key="metadata.documentMeta.uuid"
                                                    :class="{'bg-base-200': index === selectedRow}"
                                                    class="truncate text-ellipsis border-b border-base-300 hover:bg-base-200 cursor-pointer"
                                                >
                                                    <td class="pr-2 relative">
                                                        <span v-if="index !== 0" class="px-1 text-xs bg-warning text-white rounded">{{ cachedT('vat-report.outdated') }}</span>
                                                        {{ metadata.documentMeta.filename }}
                                                    </td>
                                                    <td class="pr-2"> {{ formattedDate(metadata.createdAt, 'dd-MM-yyyy HH:mm:ss') }}</td>
                                                    <td class="pr-2"> {{ formatCurrency(metadata.vatTotal, 0) }}</td>
                                                    <td class="pr-2"> {{ createdByMap[metadata.documentMeta.uuid] }}</td>
                                                    <td>
                                                        <div class="flex justify-center">
                                                            <button
                                                                id="download-vat-report"
                                                                class="pr-2 i-ph-download-simple text-lg align-bottom hover:text-xl px-12"
                                                                @click="() => storageDownloadFile(metadata.documentMeta)"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <UButton
                id="submit-vat-report"
                class="ml-2 flex items-center justify-center w-24"
                :disabled="submitDisabled"
                :loading="isSubmitting"
                :label="$t('vat-report.submit')"
                :title="isSubmitting ? $t('vat-report.submitting') : submitDisabled ? $t('vat-report.submitted') : ''"
                @click="createVatReport"
            />
        </div>
        <vat-line-comment-dialog ref="vatLineCommentDialog"/>
        <ag-grid-vue
            class="w-full h-full px-2 mt-2 min-h-60"
            :theme="agTheme"
            :column-defs="columns"
            :row-data="vatReportToTaxAuthorities.entries"
            :grid-options="gridOptions"
            @grid-ready="onGridReady"
        />
    </div>
</template>

<script lang="ts" setup>

import {computed, ComputedRef, inject, onMounted, onUnmounted, Ref, ref, shallowRef, useTemplateRef} from 'vue'
import {format} from 'date-fns'
import {useVatService} from '@/vat/vat.service'
import {AgGridVue} from 'ag-grid-vue3'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import VatReportCellRenderer from '@/components/reports/VatReportCellRenderer.vue'
import {formatCurrency, formatPercentage, formattedDate} from '@/services/formatter.service'
import {ORG_SELECTED} from '@/types/events'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {useEmitter} from '@/services/infrastructure/emitter'
import {agTheme} from '@/agUtils/ag-theme.service'

import {refreshSetting, useSetting} from '@/setting/setting.service'
import {ColDef, GridApi, GridOptions, ICellRendererParams} from 'ag-grid-community'
import {selectedOrg} from '@/tenants/org.service'
import AlertBox from '@/components/AlertBox.vue'
import {storageDownloadFile} from '@/services/document.service'
import {useCachedT} from '@/services/useCachedI18n'
import {
    bookkeepVatPeriod,
    fetchVatBalances,
    fetchVatReport,
    fetchVatTermStatuses,
    getPreviousTerm,
    VatOptionBalance,
    vatPeriodByMonth,
    VatPeriodStatus,
    VatReportResponse
} from '@/vat/vatReport.service'
import {ExtendedVatEntry, ExtendedVatReport} from '@/vat/types/ExtendedVatReport'
import RemarkCell from '@/vat/RemarkCell.vue'
import {DateRange} from '@/dateUtils/DateRange'
import VatLineCommentDialog from '@/vat/VatLineCommentDialog.vue'
import {fetchEntityAuditTrail} from '@/activity-log'
import {notifySuccess} from '@/services/infrastructure/notification.service'
import {toIsoDate} from '@/dateUtils/dateUtils'
import VatPeriodSelector from '@/vat/VatPeriodSelector.vue'

const {cachedT} = useCachedT()
const {onEvents, cleanUp} = useEmitter()
const {getValidVatOptionsForDate, getVatOptionById} = useVatService()
const vatLineCommentDialog = useTemplateRef<typeof VatLineCommentDialog>('vatLineCommentDialog')
const loading = ref(false)
const vatLiableLoaded = ref(false)
const panelId: Ref<number> | undefined = inject('panelId')
const externalRemark = ref('')
const internalRemark = ref('')
const selectedRow = ref(0)
const isSubmitting = ref(false)
const gridApi = shallowRef<GridApi>()
const vatOptionBalances = ref<VatOptionBalance[]>([])
const vatPeriodStatuses = ref<VatPeriodStatus[]>([])
const createdByMap = ref<Record<string, string>>({})

const {setting: vatLiableSetting} = useSetting<boolean>('VAT_LIABLE')
const {setting: vatAnnuallySetting} = useSetting<boolean>('VAT_ANNUALLY')
const range = ref<DateRange>(getPreviousTerm())

const VatTotalText = () => {
    return vatReportToTaxAuthorities.value.vatToPay >= 0 ? cachedT('vat-report.vat-to-pay') : cachedT('vat-report.vat-to-receive')
}

const isVatLiable: ComputedRef<Boolean> = computed<Boolean>(() => {
    const isVatLiableSetting = vatLiableSetting.value
    return isVatLiableSetting?.value ?? isVatLiableSetting?.default ?? isVatLiable.value
})

const vatReportsMetadata = ref<VatReportResponse[]>([])

const vatPeriodInfo = computed(() => {
    return vatPeriodByMonth(range.value.start.getMonth() + 1, range.value.end.getMonth() + 1)
})

const vatOptions = ref(getValidVatOptionsForDate(range.value.start))

const vatReportToTaxAuthorities = computed(() => {
    const vatReportOrder = [3, 5, 6, 7, 31, 32, 33, 51, 52, 0, 1, 11, 12, 13, 14, 15, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 20, 21, 22]
    const latestVatReport = vatReportsMetadata.value.length > 0 ? vatReportsMetadata.value[0] : undefined

    const entries: Array<ExtendedVatEntry> = vatOptionBalances.value.map(vob => {
        const vatOption = getVatOptionById(vob.vatOptionId)
        const comment = vob.comment ?? latestVatReport?.comments?.find(c => c.vatOptionId === vob.vatOptionId)?.comment
        return {
            vatTaxBase: vob.sourceSum,
            vatTaxAmount: vob.targetSum,
            remarks: comment,
            ...vatOption
        } as ExtendedVatEntry
    })
    const extendedVatReport: ExtendedVatReport = {
        entries: entries,
        taxPeriod: vatPeriodInfo.value?.periodName || '',
        year: range.value.start.getFullYear().toString(),
        vatToPay: 0
    }
    if (vatOptionBalances.value.length > 0) {
        let vatToPay = 0
        vatOptionBalances.value.forEach((vatEntry) => {
            vatToPay += Math.trunc(vatEntry.targetSum)
        })
        extendedVatReport.vatToPay = vatToPay
    }
    extendedVatReport.entries.sort((a, b) => {
        return vatReportOrder.indexOf(a.code) - vatReportOrder.indexOf(b.code)
    })
    return extendedVatReport
})

const commentsChanged = computed(() => {
    const latestVatReport = vatReportsMetadata.value.length > 0 ? vatReportsMetadata.value[0] : undefined
    if (latestVatReport === undefined) {
        return externalRemark.value.length || internalRemark.value.length || vatReportToTaxAuthorities.value.entries.some(c => c.remarks?.length)
    }

    return latestVatReport.remark !== externalRemark.value || latestVatReport.internalRemark !== internalRemark.value || vatReportToTaxAuthorities.value.entries.some(left =>
        !latestVatReport.comments.some(right => left.id === right.vatOptionId && left.remarks === right.comment)
    )
})

const isCurrentPeriodDueReporting = computed(() => {
    return vatPeriodStatuses.value.some(vps =>
        vps.startDate === toIsoDate(range.value.start) &&
        vps.endDate === toIsoDate(range.value.end) &&
        vps.status === 'DueReporting'
    )
})

const submitDisabled = computed(() => isSubmitting.value || !isVatLiable.value || (!commentsChanged.value && !isCurrentPeriodDueReporting.value))

const columns: ColDef[] = [
    {
        headerName: cachedT('vat-report.column-header.vat-code'),
        width: 500,
        resizable: true,
        cellRenderer: VatReportCellRenderer
    },
    {
        headerName: cachedT('vat-report.column-header.vat-tax-base'),
        field: 'vatTaxBase',
        width: 120,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: (params: ICellRendererParams) => formatCurrency(Math.trunc(params.value), 0)
    },
    {
        headerName: cachedT('vat-report.column-header.vat-rate'),
        field: 'displayedRate',
        width: 80,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: (params: ICellRendererParams) => formatPercentage(params.value, 2)
    },
    {
        headerName: cachedT('vat-report.column-header.vat-tax-amount'),
        field: 'vatTaxAmount',
        width: 120,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: (params: ICellRendererParams) => formatCurrency(Math.trunc(params.value), 0)
    },
    {
        headerName: cachedT('vat-report.column-header.remarks'),
        field: 'comment',
        flex: 1,
        cellRenderer: RemarkCell,
        enableCellChangeFlash: true,
        cellRendererParams: () => {
            return {
                showVatLineCommentDialog: vatLineCommentDialog.value && vatLineCommentDialog.value?.handleOpen,
                updateVatOptionComment,
            }
        }
    }
]

const gridOptions: GridOptions = {
    animateRows: true,
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => loading.value ? cachedT('vat-report.loading') : cachedT('vat-report.no-data')
    },
    enableCellTextSelection: true,
    suppressCellFocus: true,
    context: {
        panelId
    }
}

const init = async () => {
    vatPeriodStatuses.value = await fetchVatTermStatuses()
    if (vatPeriodStatuses.value.length) {
        const latest = vatPeriodStatuses.value[vatPeriodStatuses.value.length - 1]
        range.value = {
            start: new Date(latest.startDate),
            end: new Date(latest.endDate)
        }
    }
    await fetchData()
}

onMounted(async () => {
    await refreshSetting('VAT_ANNUALLY')
    await init()
    vatLiableLoaded.value = true
    onEvents([ORG_SELECTED], init)
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION], fetchData)
})

// TODO not everything needs to be refetched maybe, figure out how?
const fetchData = async () => {
    const {start, end} = range.value
    const startFormatted = format(start, 'yyyy-MM-dd')
    const endFormatted = format(end, 'yyyy-MM-dd')
    vatOptions.value = getValidVatOptionsForDate(start)
    vatReportsMetadata.value = await fetchVatReport(startFormatted, endFormatted)
    vatOptionBalances.value = await fetchVatBalances(startFormatted, endFormatted)
    vatPeriodStatuses.value = await fetchVatTermStatuses()

    const auditEntries = await Promise.all(
        vatReportsMetadata.value.map(async (report) => {
            const trail = await fetchEntityAuditTrail(report.documentMeta.uuid)
            const created = trail.find(e => e.action === 'created')
            return [report.documentMeta.uuid, created?.actor.display ?? ''] as [string, string]
        })
    )
    createdByMap.value = Object.fromEntries(auditEntries)

    const latestVatReport: VatReportResponse | undefined = vatReportsMetadata.value.length > 0 ? vatReportsMetadata.value[0] : undefined
    internalRemark.value = latestVatReport?.internalRemark ?? ''
    externalRemark.value = latestVatReport?.remark ?? ''
    latestVatReport?.comments.forEach(c => {
        const vob = vatOptionBalances.value.find(x => x.vatOptionId === c.vatOptionId)
        if (!vob) {
            return
        }
        vob.comment = c.comment
    })
}


const createVatReport = async () => {
    isSubmitting.value = true
    const {start, end} = range.value
    await bookkeepVatPeriod({
        period: {
            fromDate: format(start, 'yyyy-MM-dd'),
            toDate: format(end, 'yyyy-MM-dd')
        },
        internalRemark: internalRemark.value,
        remark: externalRemark.value,
        comments: vatOptionBalances.value
            .filter(vob => vob.comment)
            .map(vob => ({
                vatOptionId: vob.vatOptionId,
                comment: vob.comment ?? '',
                positiveSum: true
            })) // TODO remove positive sum
    })

    await fetchData()
    isSubmitting.value = false
    notifySuccess({title: 'Oppdatert', description: `Mva-melding for perioden ${cachedT('vat-report.' + vatPeriodInfo?.value?.periodName)} ${vatReportToTaxAuthorities.value.year} er bokført.`})
}

const handleTermChanged = async (newRange: DateRange) => {
    range.value = newRange
    await fetchData()
}

const onGridReady = (params: any) => {
    gridApi.value = params.api
}

const updateVatOptionComment = (vatOptionId: number, comment: string) => {
    const vatOptionBalance = vatOptionBalances.value.find(vob => vob.vatOptionId === vatOptionId)
    if (!vatOptionBalance) {
        console.error('UpdateVatOptionComment found no vat option to update')
        return
    }
    vatOptionBalance.comment = comment
}

onUnmounted(cleanUp)

</script>
<style scoped>
:deep(button[disabled]) {
    background-color: #d1d5db;
    color: #6b7280;
    border-color: #9ca3af;
}
</style>